import { useParams, Link, useSearchParams } from "react-router";
import { useCustomerById } from "../hooks/useCustomer";
import { WalletTab } from "@/features/wallets/components/WalletTab";
import { MobileAccessSection } from "../components/MobileAccessSection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  IdCard,
  Wallet as WalletIcon,
  CreditCard,
  CalendarDays,
} from "lucide-react";

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "?";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const formatKes = (value: number | null | undefined) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES",
        minimumFractionDigits: 0,
      }).format(value);

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

const Field = ({ label, children }: FieldProps) => (
  <div className="space-y-1">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <div className="text-sm font-medium break-words">{children}</div>
  </div>
);

/**
 * Per-customer detail page with tabs for Profile and Wallet. The Wallet tab is
 * the primary surface for top-ups, cash-outs, and activity (SPEC_CUSTOMER_WALLET).
 * The Profile tab groups customer info into Contact / Location / Identity & Account
 * cards so the wide canvas is used without information looking cramped.
 */
export const CustomerDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const selectedTab = searchParams.get("tab") === "wallet" ? "wallet" : "profile";
  const focusEntryId = searchParams.get("entryId") ?? undefined;
  const customerQuery = useCustomerById(id ?? "");

  if (!id) {
    return <div className="p-6 text-sm text-muted-foreground">No customer id in route.</div>;
  }

  if (customerQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  if (customerQuery.isError || !customerQuery.data) {
    return (
      <div className="p-6 space-y-2 text-sm">
        <p className="text-destructive">Could not load customer.</p>
        <Link to="/customers" className="underline text-primary">
          ← Back to customers
        </Link>
      </div>
    );
  }

  const customer = customerQuery.data;
  const addressLine = [customer.road, customer.physicalLocationName]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary text-xl font-bold ring-1 ring-primary/20">
            {initialsOf(customer.customerName)}
          </div>
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold leading-tight">{customer.customerName}</h1>
              {customer.isActive ? (
                <Badge variant="default">Active</Badge>
              ) : (
                <Badge variant="destructive">Inactive</Badge>
              )}
              {customer.isWalkIn && (
                <Badge variant="secondary" className="uppercase tracking-wide">
                  Walk-in
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" />
                {customer.mobileNo1 || "—"}
              </span>
              {customer.email && (
                <span className="inline-flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  {customer.email}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" />
                Joined {formatDate(customer.createdAt)}
              </span>
            </div>
          </div>
        </div>
        <Button asChild variant="outline">
          <Link to="/customers">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Link>
        </Button>
      </div>

      <Tabs defaultValue={selectedTab}>
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="wallet">
            <WalletIcon className="h-3.5 w-3.5 mr-1.5" />
            Wallet
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="pt-4">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {/* Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  Contact
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4">
                <Field label="Primary mobile">{customer.mobileNo1 || "—"}</Field>
                <Field label="Alternate mobile">{customer.mobileNo2 || "—"}</Field>
                <Field label="Email">{customer.email || "—"}</Field>
              </CardContent>
            </Card>

            {/* Location */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  Location
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                <Field label="County">{customer.county || "—"}</Field>
                <Field label="Town">{customer.town || "—"}</Field>
                <div className="col-span-2">
                  <Field label="Address">{addressLine || "—"}</Field>
                </div>
                {customer.gpsLocation && (
                  <div className="col-span-2">
                    <Field label="GPS">{customer.gpsLocation}</Field>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Identity & Account */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <IdCard className="h-4 w-4 text-muted-foreground" />
                  Identity & Account
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                <Field label="ID number">{customer.idNumber || "—"}</Field>
                <Field label="Payment terms">{customer.paymentTerms || "—"}</Field>
                <Field label="Credit limit">
                  <span className="inline-flex items-center gap-1.5">
                    <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                    {formatKes(customer.creditLimit)}
                  </span>
                </Field>
                <Field label="Sowing deposit override">
                  {customer.sowingPaymentThresholdOverride != null
                    ? `${customer.sowingPaymentThresholdOverride}%`
                    : "System default"}
                </Field>
              </CardContent>
            </Card>

            <MobileAccessSection customerId={id} customerType={customer.customerType ?? "Individual"} />
          </div>
        </TabsContent>

        <TabsContent value="wallet" className="pt-4">
          <WalletTab customerId={id} focusEntryId={focusEntryId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
